import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  useTheme,
  IconButton,
  Typography,
  Avatar,
  Radio,
  RadioGroup,
  FormControlLabel,
  Grid,
  Checkbox,
  FormControl,
  FormGroup,
  Autocomplete,
  FormControl as MuiFormControl,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { useFormik } from "formik";
import { ReportsSchedularSchema } from "../../component/Schemas";
import { useDispatch, useSelector } from "react-redux";
import { selectManagers ,selectFunds} from "../../Redux/Reducers/selectors";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../Redux/Reducers/uiSlice";
import { hashGetReportType, ReportType } from "../../component/Methods";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";

const fontFamily = `'Inter', 'SF Pro Display', 'Roboto', 'Arial', sans-serif`;

const ReportsDialog = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const funds = useSelector(selectFunds);
  const managers = useSelector(selectManagers);
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedDays, setSelectedDays] = useState({
    Monday: true,
    Tuesday: true,
    Wednesday: true,
    Thursday: true,
    Friday: true,
    Saturday: false,
    Sunday: false,
  });

  const FundList = (funds?.map((i) => i?.abbreviation) || [])?.sort();

  useEffect(() => {
    const currentTime = new Date();
    const hours = String(currentTime.getHours()).padStart(2, "0");
    const minutes = String(currentTime.getMinutes()).padStart(2, "0");
    const seconds = String(currentTime.getSeconds()).padStart(2, "0");
    setSelectedTime(`${hours}:${minutes}:${seconds}`);
  }, []);

  const formik = useFormik({
    initialValues: {
      name: "",
      scheduleType: "daily",
      type: "",
      selectedTime: selectedTime,
      selectedDays: selectedDays,
      selectedFunds: [],
    },
    validationSchema: ReportsSchedularSchema,
    onSubmit: (values) => {
      const managerId = managers[0]?.managerId || "";
      const selectedFundsAbbr =
        values.selectedFunds.length > 0 ? values.selectedFunds : FundList;
      const fundArray = funds?.filter((fund) =>
        selectedFundsAbbr.includes(fund?.abbreviation)
      );
      const fundIds = fundArray?.map((fund) => fund.fundId);
      const frequency = values.scheduleType === "daily" ? 0 : 1;
      let daysBitmask = 0;
      Object.keys(values.selectedDays).forEach((day, index) => {
        if (values.selectedDays[day]) {
          daysBitmask += Math.pow(2, index);
        }
      });

      const requestData = {
        action: "create_report",
        data: {
          fundIds: fundIds,
          name: values.name,
          type: Number(hashGetReportType(values.type)),
          managerId: managerId,
          schedule: {
            frequency: frequency,
            days: daysBitmask,
          },
        },
      };
      sendWebSocketMessage(JSON.stringify(requestData));
      onClose();
      formik.resetForm();
      dispatch(addActiveRequests("create_report"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg(`Creating ${values?.type} Report`));
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  const handleFundChange = (event, newValue) => {
    formik.setFieldValue("selectedFunds", newValue);
  };

  const handleTypeChange = (event, newValue) => {
    formik.setFieldValue("type", newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 4px 24px 0 rgba(16,30,54,0.10)',
          fontFamily,
          p: 0,
        },
      }}
    >
      <Box sx={{ px: 4, pt: 3, pb: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
              <DescriptionIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontSize: 22, fontFamily, lineHeight: 1.2 }}
              >
                Add New Report
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, fontFamily, mt: 0.5 }}
              >
                Enter report details to create a new scheduled report.
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleCancel} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      
      <DialogContent sx={{ pt: 2, pb: 2, px: 4 }}>
        <Stack spacing={3}>
          <TextField
            label="Report Name"
            placeholder="Report Name"
            id="name"
            name="name"
            variant="outlined"
            value={formik.values.name}
            onChange={formik.handleChange}
            fullWidth
            required
            error={formik.touched.name && !!formik.errors.name}
            helperText={formik.touched.name && formik.errors.name}
            InputLabelProps={{ shrink: true, sx: { fontWeight: 600, fontFamily } }}
            inputProps={{ style: { fontFamily } }}
          />

          <MuiFormControl fullWidth>
            <Autocomplete
              options={ReportType}
              value={formik.values.type}
              onChange={handleTypeChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Report Type"
                  variant="outlined"
                  fullWidth
                  required
                  error={formik.touched.type && !!formik.errors.type}
                  helperText={formik.touched.type && formik.errors.type}
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600, fontFamily } }}
                  inputProps={{ ...params.inputProps, style: { fontFamily } }}
                />
              )}
            />
          </MuiFormControl>

          <MuiFormControl fullWidth>
            <Autocomplete
              multiple
              options={FundList}
              value={formik.values.selectedFunds}
              onChange={handleFundChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Funds"
                  variant="outlined"
                  fullWidth
                  error={
                    formik.touched.selectedFunds && !!formik.errors.selectedFunds
                  }
                  helperText={
                    formik.touched.selectedFunds && formik.errors.selectedFunds
                  }
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600, fontFamily } }}
                  inputProps={{ ...params.inputProps, style: { fontFamily } }}
                />
              )}
            />
          </MuiFormControl>

          <Box>
            <Typography variant="h6" sx={{ marginBottom: 2, fontSize: "18px", fontWeight: 600, fontFamily }}>
              Schedule Type
            </Typography>
            <RadioGroup
              value={formik.values.scheduleType}
              onChange={formik.handleChange}
              name="scheduleType"
              aria-label="schedule-type"
              row
            >
              <FormControlLabel value="daily" control={<Radio />} label="Daily" />
              <FormControlLabel value="hourly" control={<Radio />} label="Hourly" />
            </RadioGroup>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ marginBottom: 2, fontSize: "18px", fontWeight: 600, fontFamily }}>
              Days
            </Typography>
            <FormGroup row>
              {Object.keys(formik.values.selectedDays).map((day) => (
                <FormControlLabel
                  key={day}
                  control={
                    <Checkbox
                      checked={formik.values.selectedDays[day]}
                      onChange={formik.handleChange}
                      name={`selectedDays.${day}`}
                    />
                  }
                  label={day}
                />
              ))}
            </FormGroup>

            {formik.touched.selectedDays && formik.errors.selectedDays && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {formik.errors.selectedDays}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{ borderRadius: 1, fontWeight: 600, fontFamily, textTransform: 'none', px: 3, minWidth: 120 }}
        >
          Cancel
        </Button>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          color="primary"
          sx={{ borderRadius: 1, fontWeight: 600, fontFamily, textTransform: 'none', px: 3, minWidth: 120 }}
        >
          Create Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportsDialog; 